import { useEffect, useRef, type ReactNode } from 'react'
import { ChevronLeft, ChevronRight } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import { Box, Button, Link, Typography } from '@mui/material'
import { useOnboarding } from '../context/OnboardingContext'
import { OnboardingPhaseProgress } from './OnboardingPhaseProgress'
import { Notice } from '../components/ui'
import { clearClientOnboardingComplete } from '../lib/onboardingPortalFlags'
import { daysRemaining } from '../lib/session'
import { ONBOARDING_SESSION_DAYS } from '../lib/prototypeSettings'
import { SUPPORT_EMAIL } from '../data/mockSeed'
import { AppRoutes } from '../../helper/appRoutes'
import { MISC_PRIMARY, MISC_PRIMARY_HOVER } from '../theme/onboardingTheme'

function primaryLabel(location: ReturnType<typeof useOnboarding>['location']): string {
  if (location.kind === 'entity') {
    return location.section === 'credentials' ? 'Save & Return to Entity List' : 'Save & Continue'
  }
  if (location.step === 'welcome') return 'Start Onboarding'
  if (location.step === 'review') return 'Send to MISC'
  return 'Continue'
}

export function WizardChrome({ children }: { children: ReactNode }) {
  const navigate = useNavigate()
  const { state, dispatch, location, advance, back, canGoBack, blockers, showBlockers } = useOnboarding()
  const blockersRef = useRef<HTMLDivElement>(null)
  const isComplete = location.kind === 'client' && location.step === 'complete'
  const wideLayout =
    location.kind === 'entity' ||
    (location.kind === 'client' && ['entitySetup', 'entityDashboard', 'review'].includes(location.step))

  const contentWidthSx = wideLayout ? { width: '100%', maxWidth: 1180, mx: 'auto' } : { mx: 'auto', maxWidth: 820 }

  useEffect(() => {
    if (showBlockers) blockersRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' })
  }, [showBlockers, blockers.length])

  const saveAndExit = () => {
    dispatch({ type: 'TOAST', message: 'Progress saved. Log back in any time to pick up where you left off.' })
    navigate(AppRoutes.client.home)
  }

  return (
    <Box sx={{ display: 'flex', minHeight: 0, minWidth: 0, flex: 1, flexDirection: 'column' }}>
      <Box sx={{ borderBottom: '1px solid #e2e8f0', backgroundColor: '#ffffff', px: { xs: 2, lg: 5 }, py: 2 }}>
        <Box sx={contentWidthSx}>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
            <Typography sx={{ fontSize: 12, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em', color: '#64748b' }}>
              Onboarding · {state.clientName}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Typography sx={{ fontSize: 12, fontWeight: 600, color: '#475569' }}>
                {daysRemaining(state.startedAt)} of {ONBOARDING_SESSION_DAYS} days remaining
              </Typography>
              <Button
                type="button"
                onClick={() => {
                  clearClientOnboardingComplete()
                  dispatch({ type: 'RESET' })
                }}
                sx={{
                  minWidth: 0,
                  p: 0,
                  fontSize: 12,
                  fontWeight: 600,
                  color: '#64748b',
                  '&:hover': { color: MISC_PRIMARY, textDecoration: 'underline', backgroundColor: 'transparent' },
                }}
              >
                Reset demo
              </Button>
            </Box>
          </Box>
          <Typography sx={{ mt: 0.5, fontSize: '1.25rem', fontWeight: 600, color: MISC_PRIMARY }}>
            Welcome to MISC! Please complete your onboarding
          </Typography>
          <Typography sx={{ mt: 0.5, fontSize: 14, color: '#475569' }}>
            Questions at any time? Email{' '}
            <Link href={`mailto:${SUPPORT_EMAIL}`} sx={{ fontWeight: 500, color: MISC_PRIMARY }}>
              {SUPPORT_EMAIL}
            </Link>
            .
          </Typography>
          <OnboardingPhaseProgress />
        </Box>
      </Box>

      <Box sx={{ flex: 1, overflowY: 'auto', px: { xs: 2, lg: 5 }, py: 4, pb: 14 }}>
        <Box sx={contentWidthSx}>
          {children}
          {!isComplete && showBlockers ? (
            <Box ref={blockersRef} role="alert" sx={{ mt: 3 }}>
              <Notice tone="error">
                <Typography sx={{ fontSize: 14, fontWeight: 600, color: '#991b1b' }}>
                  Please complete the following before continuing:
                </Typography>
                <Box component="ul" sx={{ m: 0, mt: 0.5, pl: 2.5 }}>
                  {blockers.map((b) => (
                    <li key={b}>{b}</li>
                  ))}
                </Box>
              </Notice>
            </Box>
          ) : null}
        </Box>
      </Box>

      {!isComplete ? (
        <Box
          component="footer"
          sx={{
            position: 'sticky',
            bottom: 0,
            zIndex: 20,
            borderTop: '1px solid #e2e8f0',
            backgroundColor: 'rgba(255,255,255,0.95)',
            backdropFilter: 'blur(8px)',
            px: { xs: 2, lg: 5 },
            py: 2,
          }}
        >
          <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', gap: 1.5, ...contentWidthSx }}>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1 }}>
              <Button
                type="button"
                variant="outlined"
                onClick={back}
                disabled={!canGoBack}
                startIcon={<ChevronLeft size={16} />}
                sx={{ borderColor: '#cbd5e1', color: '#334155', px: 2, py: 1.25, '&:hover': { backgroundColor: '#f8fafc' }, '&.Mui-disabled': { opacity: 0.4 } }}
              >
                Back
              </Button>
              <Button
                type="button"
                variant="outlined"
                onClick={saveAndExit}
                sx={{
                  borderColor: 'rgba(37, 99, 235, 0.5)',
                  backgroundColor: '#eff6ff',
                  color: MISC_PRIMARY,
                  fontWeight: 600,
                  px: 2,
                  py: 1.25,
                  '&:hover': { backgroundColor: 'rgba(219, 234, 254, 0.8)' },
                }}
              >
                Save &amp; Exit
              </Button>
            </Box>
            <Button
              type="button"
              variant="contained"
              onClick={advance}
              endIcon={<ChevronRight size={16} />}
              sx={{
                flexShrink: 0,
                backgroundColor: MISC_PRIMARY,
                px: 2.5,
                py: 1.25,
                boxShadow: 1,
                '&:hover': { backgroundColor: MISC_PRIMARY_HOVER },
                '&.Mui-disabled': { opacity: 0.4 },
              }}
            >
              {primaryLabel(location)}
            </Button>
          </Box>
        </Box>
      ) : null}
    </Box>
  )
}
